/**
 * /admin/activity — owner-console activity feed.
 *
 * Two tabs:
 *   Promotions — MarketplacePromotion rows (every pack-publish fan-out
 *     across WhatsApp / email / in-app / digest with sent/skipped/failed
 *     status). Existing F5 surface.
 *   Admin audit — ZenAdminAudit rows (privileged actions across all
 *     consoles: refund approvals, dispute responses, impersonation
 *     start/stop, credit-note issuance, scorer moderation, etc.) plus
 *     CSV export. Added P1 — 2026-06-08.
 */
import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw, MessageSquare, Mail, Bell, Send, AlertTriangle,
  Download, Shield, ChevronDown, ChevronRight,
} from "lucide-react";
import {
  listMarketplacePromotions,
  listAdminAudit,
  downloadAdminAuditCsv,
} from "../../api/owner.api";
import { toArray } from "../../utils/toArray";

const STATUS_COLOR = {
  sent:    "text-emerald-300",
  partial: "text-amber-300",
  skipped: "text-text-secondary",
  failed:  "text-red-300",
  queued:  "text-blue-300",
};

const CHANNEL_ICON = {
  whatsapp: MessageSquare,
  email:    Mail,
  in_app:   Bell,
  digest:   Send,
};

const CHANNELS = ["", "whatsapp", "email", "in_app", "digest"];

const AUDIT_TARGET_TYPES = [
  "", "USER", "ORG", "SUBSCRIPTION", "REFUND", "DISPUTE", "CREDIT_NOTE",
  "INVOICE", "IMPERSONATION",
];

const TABS = [
  { key: "promotions", label: "Promotions",
    hint: "Pack-publish fan-outs across WhatsApp / email / in-app." },
  { key: "audit",      label: "Admin audit",
    hint: "ZenAdminAudit ledger — every privileged action." },
];

export default function AdminActivity() {
  const [tab, setTab] = useState("promotions");
  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Activity log</h1>
          <p className="text-sm text-text-secondary mt-1">
            Marketplace promotion fan-outs + admin audit trail. Both feeds
            are append-only.
          </p>
        </div>
      </header>

      <div className="flex flex-wrap gap-2 border-b border-border-default pb-3">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              title={t.hint}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                active
                  ? "bg-button-primary text-white border-button-primary"
                  : "bg-bg-primary border-border-default text-text-secondary hover:text-text-primary hover:border-text-secondary"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "promotions" ? <PromotionsTab /> : <AuditTab />}
    </div>
  );
}


function PromotionsTab() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [channel, setChannel] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function fetchPage(p) {
    setLoading(true);
    setError(null);
    try {
      const { data } = await listMarketplacePromotions({
        page: p,
        channel: channel || undefined,
      });
      setRows(toArray(data));
      setCount(data?.count || 0);
      if (p === 1 && page !== 1) setPage(1);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchPage(1); /* eslint-disable-next-line */ }, [channel]);
  useEffect(() => { fetchPage(page); /* eslint-disable-next-line */ }, [page]);

  const totalPages = Math.max(1, Math.ceil(count / 25));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {CHANNELS.map((c) => {
            const active = channel === c;
            const label = c || "All channels";
            return (
              <button
                key={c || "all"}
                onClick={() => setChannel(c)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  active
                    ? "bg-button-primary text-white border-button-primary"
                    : "bg-bg-primary border-border-default text-text-secondary hover:text-text-primary hover:border-text-secondary"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => fetchPage(page)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-sm hover:bg-bg-secondary"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 p-3 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="rounded-lg border border-border-default bg-bg-primary overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Marketplace promotion fan-outs</caption>
          <thead className="bg-table-header">
            <tr className="text-left text-[11px] uppercase tracking-widest text-text-secondary">
              <th scope="col" className="px-3 py-2">When</th>
              <th scope="col" className="px-3 py-2">Pack</th>
              <th scope="col" className="px-3 py-2">Channel</th>
              <th scope="col" className="px-3 py-2">Status</th>
              <th scope="col" className="px-3 py-2 text-right">Recipients</th>
              <th scope="col" className="px-3 py-2 hidden md:table-cell">Actor</th>
              <th scope="col" className="px-3 py-2 hidden md:table-cell">Notes</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-text-secondary">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-text-secondary">
                Nothing published yet. Use <Link to="/admin/marketplace" className="text-button-primary hover:underline">/admin/marketplace</Link> to ship the first pack.
              </td></tr>
            )}
            {rows.map((r) => {
              const Icon = CHANNEL_ICON[r.channel] || Send;
              return (
                <tr key={r.id} className="border-t border-border-default">
                  <td className="px-3 py-2 text-xs text-text-secondary tabular-nums">
                    {new Date(r.sent_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 truncate max-w-[180px]">{r.pack_name || "—"}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1 text-xs font-mono">
                      <Icon className="w-3 h-3 text-text-secondary" />
                      {r.channel}
                    </span>
                  </td>
                  <td className={`px-3 py-2 text-xs font-semibold ${STATUS_COLOR[r.status] || ""}`}>
                    {r.status}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{r.recipient_count}</td>
                  <td className="px-3 py-2 text-xs text-text-secondary truncate max-w-[160px] hidden md:table-cell">
                    {r.actor_email || "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-text-secondary truncate max-w-[200px] hidden md:table-cell">
                    {r.notes || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-text-secondary">
        <div>Page {page} of {totalPages} · {count} entries</div>
        <div className="flex gap-1">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 rounded border border-border-default disabled:opacity-40 hover:bg-bg-secondary"
          >Prev</button>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-3 py-1 rounded border border-border-default disabled:opacity-40 hover:bg-bg-secondary"
          >Next</button>
        </div>
      </div>
    </div>
  );
}


function AuditTab() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [targetType, setTargetType] = useState("");
  const [actionContains, setActionContains] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const filterParams = () => ({
    search:         search || undefined,
    target_type:    targetType || undefined,
    action_contains: actionContains || undefined,
  });

  async function fetchPage(p) {
    setLoading(true);
    setError(null);
    try {
      const { data } = await listAdminAudit({ page: p, ...filterParams() });
      setRows(toArray(data));
      setCount(data?.count || 0);
      if (p === 1 && page !== 1) setPage(1);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => fetchPage(1), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, targetType, actionContains]);
  useEffect(() => { fetchPage(page); /* eslint-disable-next-line */ }, [page]);

  async function handleCsv() {
    setDownloading(true);
    setError(null);
    try {
      await downloadAdminAuditCsv(filterParams());
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "CSV download failed");
    } finally {
      setDownloading(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(count / 25));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search actor / target / action…"
            className="px-3 py-2 rounded-md bg-input border border-border-default text-sm w-64 focus:outline-none focus:border-button-primary"
          />
          <select
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
            className="px-2 py-2 rounded-md bg-input border border-border-default text-sm"
          >
            {AUDIT_TARGET_TYPES.map((t) => (
              <option key={t || "all"} value={t}>
                {t || "Any target type"}
              </option>
            ))}
          </select>
          <input
            value={actionContains}
            onChange={(e) => setActionContains(e.target.value)}
            placeholder="action contains… (e.g. refund)"
            className="px-3 py-2 rounded-md bg-input border border-border-default text-sm w-56 focus:outline-none focus:border-button-primary"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleCsv}
            disabled={downloading}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-emerald-500/40 text-emerald-300 text-sm hover:bg-emerald-500/10 disabled:opacity-50"
            title="Stream up to 5,000 matching rows as CSV"
          >
            <Download className={`w-3.5 h-3.5 ${downloading ? "animate-pulse" : ""}`} />
            {downloading ? "Preparing…" : "Export CSV"}
          </button>
          <button
            onClick={() => fetchPage(page)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-sm hover:bg-bg-secondary"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 p-3 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="rounded-lg border border-border-default bg-bg-primary overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Owner-console audit log</caption>
          <thead className="bg-table-header">
            <tr className="text-left text-[11px] uppercase tracking-widest text-text-secondary">
              <th scope="col" className="w-6 px-1 py-2"><span className="sr-only">Expand</span></th>
              <th scope="col" className="px-3 py-2">When</th>
              <th scope="col" className="px-3 py-2">Actor</th>
              <th scope="col" className="px-3 py-2">Action</th>
              <th scope="col" className="px-3 py-2 hidden md:table-cell">Target type</th>
              <th scope="col" className="px-3 py-2 hidden md:table-cell">Target</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-text-secondary">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-text-secondary">
                No audit rows in the last 30 days for these filters.
              </td></tr>
            )}
            {rows.map((r) => {
              const expanded = expandedId === r.id;
              const hasDetail = r.detail && Object.keys(r.detail).length > 0;
              return (
                <Fragment key={r.id}>
                  <tr className="border-t border-border-default hover:bg-bg-secondary">
                    <td className="px-1 py-2 text-text-secondary">
                      {hasDetail ? (
                        <button
                          onClick={() => setExpandedId(expanded ? null : r.id)}
                          aria-expanded={expanded}
                          aria-label={`${expanded ? "Collapse" : "Expand"} audit detail for ${r.action}`}
                          className="p-0.5 hover:text-text-primary"
                        >
                          {expanded
                            ? <ChevronDown className="w-3.5 h-3.5" />
                            : <ChevronRight className="w-3.5 h-3.5" />}
                        </button>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary tabular-nums whitespace-nowrap">
                      {new Date(r.timestamp).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-mono">{r.actor_email || `id ${r.actor_id}`}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 text-xs">
                        <Shield className="w-3 h-3 text-text-secondary" />
                        <code className="text-xs">{r.action}</code>
                      </span>
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell text-xs text-text-secondary">
                      {r.target_type || "—"}
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell text-xs">
                      {r.target_user_email ? (
                        <Link
                          to={`/admin/users?segment=all&search=${encodeURIComponent(r.target_user_email)}`}
                          className="text-button-primary hover:underline font-mono"
                        >
                          {r.target_user_email}
                        </Link>
                      ) : r.target_id ? (
                        <code className="text-text-secondary">{r.target_id}</code>
                      ) : (
                        <span className="text-text-secondary">—</span>
                      )}
                    </td>
                  </tr>
                  {expanded && hasDetail && (
                    <tr className="border-t border-border-default bg-bg-secondary/40">
                      <td colSpan={6} className="px-12 py-2">
                        <pre className="text-[11px] font-mono whitespace-pre-wrap text-text-secondary">
                          {JSON.stringify(r.detail, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-text-secondary">
        <div>Page {page} of {totalPages} · {count} entries · default lookback 30 days</div>
        <div className="flex gap-1">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 rounded border border-border-default disabled:opacity-40 hover:bg-bg-secondary"
          >Prev</button>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-3 py-1 rounded border border-border-default disabled:opacity-40 hover:bg-bg-secondary"
          >Next</button>
        </div>
      </div>
    </div>
  );
}
